import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, CircleDashed, Download, FlaskConical, Landmark, Plug } from "lucide-react";
import { API, api } from "../../lib/api";
import { apiError, useAuth, authDownload } from "../../lib/auth";
import { Panel, NeonButton } from "../common";

const ST = {
  connected: ["Connected", "#CCFF00", CheckCircle2], configured: ["Configured · awaiting events", "#37E5D7", Plug],
  simulated: ["Simulated data · add API key to go live", "#FFB800", FlaskConical], not_connected: ["Not connected", "#F87171", CircleDashed],
};
const inp = "bg-field border border-fieldline px-3 py-2 text-sm w-full focus:outline-none focus:border-brand";

export const Integrations = () => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/merchant/integrations").then((r) => setD(r.data)); }, []);
  return (
    <Panel title="Integrations" testid="mp-integrations">
      <ul className="space-y-3">
        {d?.items.map((x) => {
          const [label, c, Icon] = ST[x.status];
          return (
            <li key={x.name} className="flex items-center gap-3" data-testid={`integration-${x.name.toLowerCase()}`}>
              <Icon className="w-4 h-4 shrink-0" style={{ color: c }} />
              <span className="text-sm w-20">{x.name}</span>
              <span className="font-mono text-[10px] flex-1" style={{ color: c }}>{label}</span>
              <span className="font-mono text-[10px] text-white/40">{x.disputes} disputes</span>
            </li>
          );
        })}
      </ul>
      <Link to="/app/integrations" className="block mt-4 font-mono text-[10px] text-brand hover:underline" data-testid="mp-open-hub">OPEN INTEGRATION HUB · DEPLOY WIZARD →</Link>
      <Link to="/app/connect" className="block mt-2 font-mono text-[10px] text-brand hover:underline" data-testid="mp-open-connect">CONNECT &amp; SETUP · WHOP + VERIFICATION →</Link>
    </Panel>
  );
};

export const Downloads = () => (
  <Panel title="Downloads" testid="mp-downloads">
    <ul className="space-y-3">
      {[["Monthly recovery report", "Disputes, wins, recovered amount and the 15% fee by month (CSV)", "/merchant/report.csv", "download-report"],
        ["Audit trail", "Every action, hash-chained, for compliance and issuer review (CSV)", "/merchant/audit.csv", "download-audit"]].map(([t, s, p, id]) => (
        <li key={id}><a href={`#${id}`} onClick={(e) => { e.preventDefault(); authDownload(`${API}${p}`, p.split("/").pop()); }} className="flex items-start gap-3 group" data-testid={id}>
          <Download className="w-4 h-4 text-brand mt-0.5" /><span><span className="text-sm group-hover:text-brand transition-colors">{t}</span><span className="block text-[11px] text-white/45">{s}</span></span>
        </a></li>
      ))}
    </ul>
    <p className="text-[11px] text-white/40 mt-4">Issuer-ready evidence PDFs are on each dispute's page.</p>
  </Panel>
);

export const Payout = () => {
  const { isOwner } = useAuth();
  const [p, setP] = useState(null);
  const [f, setF] = useState({ account_holder: "", bank_name: "", routing_number: "", account_number: "", deposit_preference: "monthly" });
  useEffect(() => { api.get("/merchant/payout").then((r) => setP(r.data)); }, []);
  const save = async (e) => {
    e.preventDefault();
    try { const { data } = await api.put("/merchant/payout", f); setP(data); setF({ ...f, routing_number: "", account_number: "" }); toast.success("Payout details saved (masked)"); }
    catch (err) { toast.error(apiError(err)); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Panel title="Payout details" testid="mp-payout">
      <p className="font-mono text-[10px] tracking-wider text-[#FFB800] mb-3 flex items-center gap-2" data-testid="payout-coming-soon"><Landmark className="w-3.5 h-3.5" /> PAYOUT CONNECTION COMING SOON · DETAILS STORED MASKED</p>
      {p?.configured && (
        <dl className="grid grid-cols-2 gap-2 font-mono text-xs mb-4" data-testid="payout-saved">
          {[["Holder", p.account_holder], ["Bank", p.bank_name], ["Routing", p.routing_masked], ["Account", p.account_masked], ["Deposits", p.deposit_preference?.replace("_", " ")]].map(([k, v]) => (
            <div key={k}><dt className="text-white/40">{k}</dt><dd>{v}</dd></div>))}
        </dl>
      )}
      {isOwner ? (
        <form onSubmit={save} className="grid grid-cols-2 gap-3" data-testid="payout-form">
          <input required placeholder="Account holder" value={f.account_holder} onChange={set("account_holder")} className={inp} data-testid="payout-holder" />
          <input required placeholder="Bank name" value={f.bank_name} onChange={set("bank_name")} className={inp} data-testid="payout-bank" />
          <input required placeholder="Routing number" value={f.routing_number} onChange={set("routing_number")} className={inp} data-testid="payout-routing" />
          <input required placeholder="Account number" value={f.account_number} onChange={set("account_number")} className={inp} data-testid="payout-account" />
          <select value={f.deposit_preference} onChange={set("deposit_preference")} className={inp} data-testid="payout-preference">
            <option value="per_recovery">Deposit per recovery</option><option value="weekly">Weekly</option><option value="monthly">Monthly</option>
          </select>
          <NeonButton type="submit" data-testid="payout-save-btn">{p?.configured ? "Update" : "Save"} details</NeonButton>
        </form>
      ) : <p className="text-xs text-white/40">Only the workspace owner can edit payout details.</p>}
    </Panel>
  );
};
